import { useState, useEffect } from 'react';
import { Bell, Globe, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useNotificationPrefs, useUpdateNotificationPrefs, useTenantSettings, useUpdateTenantSettings, useComplianceNotificationSettings, useUpdateComplianceNotificationSettings } from '@/api/hooks/useSettings';
import { useAuthStore } from '@/store/auth.store';
import { RequireRole } from '@/components/auth/RequireRole';
import type { NotificationPreferences } from '@/api/types/settings.types';

const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Amsterdam',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
  'UTC',
];

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${checked ? 'bg-accent-cyan' : 'bg-bg-surface-2'}`}
    >
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-[18px]' : 'translate-x-1'}`} />
    </button>
  );
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function NotificationsPage() {
  return (
    <RequireRole roles={['risk_manager', 'super_admin']} label="Notifications">
      <NotificationsPageBody />
    </RequireRole>
  );
}

function NotificationsPageBody() {
  const { data: prefs, isLoading } = useNotificationPrefs();
  const updateMutation = useUpdateNotificationPrefs();
  const [form, setForm] = useState<Partial<NotificationPreferences>>({});

  const { hasAnyRole } = useAuthStore();
  const canEditTenant = hasAnyRole(['risk_manager', 'admin', 'super_admin']);
  const canManageTenantSettings = hasAnyRole(['risk_manager', 'super_admin']);
  const isSuperAdmin = hasAnyRole(['super_admin']);

  // Compliance reminder settings (Risk Manager + Super Admin section)
  const { data: complianceSettings } = useComplianceNotificationSettings();
  const updateComplianceMutation = useUpdateComplianceNotificationSettings();
  const [reminderForm, setReminderForm] = useState({
    compliance_notifications_enabled: true,
    daily_digest_send_hour: 9,
    reminder_cadence_days: 7,
    business_days: [1, 2, 3, 4, 5] as number[],
  });
  useEffect(() => {
    if (complianceSettings) {
      setReminderForm({
        compliance_notifications_enabled: complianceSettings.compliance_notifications_enabled,
        daily_digest_send_hour: complianceSettings.daily_digest_send_hour,
        reminder_cadence_days: complianceSettings.reminder_cadence_days,
        business_days: complianceSettings.business_days ?? [1, 2, 3, 4, 5],
      });
    }
  }, [complianceSettings]);
  const { data: tenantSettings } = useTenantSettings();
  const updateTenantMutation = useUpdateTenantSettings();
  const [timezone, setTimezone] = useState('America/New_York');
  const [sessionTimeout, setSessionTimeout] = useState(1440);
  const [requireReauth, setRequireReauth] = useState(false);

  useEffect(() => {
    if (tenantSettings?.timezone) setTimezone(tenantSettings.timezone);
    if (tenantSettings?.session_timeout_minutes != null) setSessionTimeout(tenantSettings.session_timeout_minutes);
    if (tenantSettings?.require_reauth_for_sensitive_actions != null) setRequireReauth(tenantSettings.require_reauth_for_sensitive_actions);
  }, [tenantSettings]);

  useEffect(() => {
    if (prefs) setForm(prefs);
  }, [prefs]);

  function handleSave(e: React.FormEvent) {
    e.preventDefault();
    updateMutation.mutate(form, {
      onSuccess: () => toast.success('Preferences saved'),
      onError: () => toast.error('Failed to save preferences'),
    });
  }

  if (isLoading) return <div className="space-y-4"><Skeleton className="h-40 w-full" /><Skeleton className="h-40 w-full" /></div>;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Notifications</h1>
        <p className="mt-1 text-sm text-text-secondary">Email notification preferences and distribution lists</p>
      </div>

      {/* Organization timezone */}
      <Card>
        <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Globe className="h-4 w-4" /> Organization Settings</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label>Timezone</Label>
            <p className="text-xs text-text-secondary">Used for scheduled reports</p>
            <div className="flex items-center gap-3">
              <select
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                disabled={!canEditTenant}
                className="flex-1 rounded-md border border-border-subtle bg-bg-surface-2 px-3 py-2 text-sm text-text-primary focus:outline-none disabled:opacity-50"
              >
                {COMMON_TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
              {canEditTenant && (
                <Button
                  type="button"
                  size="sm"
                  disabled={updateTenantMutation.isPending}
                  onClick={() => updateTenantMutation.mutate({ timezone }, {
                    onSuccess: () => toast.success('Timezone saved'),
                    onError: () => toast.error('Failed to save timezone'),
                  })}
                >
                  Save
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Distribution lists */}
        <Card>
          <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Bell className="h-4 w-4" /> Distribution Lists</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-text-secondary">Use distribution lists — manage who receives alerts behind the DL</p>
            {[
              { key: 'critical_alerts_dl', label: 'Critical Alerts DL' },
              { key: 'high_alerts_dl', label: 'High Alerts DL' },
              { key: 'general_updates_dl', label: 'General Updates DL' },
              { key: 'product_updates_dl', label: 'Product Updates DL' },
            ].map(({ key, label }) => (
              <div key={key} className="space-y-1.5">
                <Label>{label}</Label>
                <Input
                  type="email"
                  value={(form as Record<string, string | undefined>)[key] ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value || undefined }))}
                  placeholder="dl-alerts@company.com"
                />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Digest settings */}
        <Card>
          <CardHeader><CardTitle className="text-sm">Digest Settings</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-primary">Enable digest emails</p>
                <p className="text-xs text-text-secondary">Receive a summary of activity</p>
              </div>
              <Toggle
                checked={form.digest_enabled ?? false}
                onChange={(v) => setForm((f) => ({ ...f, digest_enabled: v }))}
              />
            </div>
            {form.digest_enabled && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Frequency</Label>
                  <select
                    value={form.digest_frequency ?? 'daily'}
                    onChange={(e) => setForm((f) => ({ ...f, digest_frequency: e.target.value as 'daily' | 'weekly' }))}
                    className="w-full rounded-md border border-border-subtle bg-bg-surface-2 px-3 py-2 text-sm text-text-primary focus:outline-none"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Time</Label>
                  <input
                    type="time"
                    value={form.digest_time ?? '08:00'}
                    onChange={(e) => setForm((f) => ({ ...f, digest_time: e.target.value }))}
                    className="w-full rounded-md border border-border-subtle bg-bg-surface-2 px-3 py-2 text-sm text-text-primary focus:outline-none"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alert categories */}
        <Card>
          <CardHeader><CardTitle className="text-sm">Alert Categories</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {[
              { key: 'assessment_notifications', label: 'Assessment notifications', desc: 'New assessments, submissions, approvals' },
              { key: 'security_notifications', label: 'Security notifications', desc: 'Score drops, critical findings, KEV alerts' },
              { key: 'compliance_notifications', label: 'Compliance notifications', desc: 'Policy reviews, exception expiry, evidence gaps' },
              { key: 'product_update_notifications', label: 'Product updates', desc: 'New features and platform announcements' },
            ].map(({ key, label, desc }) => (
              <div key={key} className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-primary">{label}</p>
                  <p className="text-xs text-text-secondary">{desc}</p>
                </div>
                <Toggle
                  checked={(form as Record<string, boolean | undefined>)[key] ?? true}
                  onChange={(v) => setForm((f) => ({ ...f, [key]: v }))}
                />
              </div>
            ))}
          </CardContent>
        </Card>

        <Button type="submit" disabled={updateMutation.isPending}>Save Preferences</Button>
      </form>

      {/* Compliance Reminder Settings — Risk Manager + Super Admin only */}
      {canManageTenantSettings && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Bell className="h-4 w-4" /> Compliance Assignment Reminders
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-text-secondary">
              Controls when reminder emails are sent to compliance control assignees.
            </p>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-primary">Enable compliance notifications</p>
                <p className="text-xs text-text-secondary">Sends assigned / reminder / digest emails to control owners</p>
              </div>
              <Toggle
                checked={reminderForm.compliance_notifications_enabled}
                onChange={(v) => setReminderForm((f) => ({ ...f, compliance_notifications_enabled: v }))}
              />
            </div>

            {reminderForm.compliance_notifications_enabled && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Daily digest send hour</Label>
                    <select
                      value={reminderForm.daily_digest_send_hour}
                      onChange={(e) => setReminderForm((f) => ({ ...f, daily_digest_send_hour: Number(e.target.value) }))}
                      className="w-full rounded-md border border-border-subtle bg-bg-surface-2 px-3 py-2 text-sm text-text-primary focus:outline-none"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>
                          {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-text-secondary">Reminders are batched and sent once per day at this hour (organisation timezone)</p>
                  </div>

                  <div className="space-y-1.5">
                    <Label>Reminder cadence (days)</Label>
                    <select
                      value={reminderForm.reminder_cadence_days}
                      onChange={(e) => setReminderForm((f) => ({ ...f, reminder_cadence_days: Number(e.target.value) }))}
                      className="w-full rounded-md border border-border-subtle bg-bg-surface-2 px-3 py-2 text-sm text-text-primary focus:outline-none"
                    >
                      {[1, 3, 5, 7, 14, 21, 30].map((d) => (
                        <option key={d} value={d}>{d} day{d !== 1 ? 's' : ''}</option>
                      ))}
                    </select>
                    <p className="text-xs text-text-secondary">How often reminders repeat until the control is submitted</p>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Business days</Label>
                  <div className="flex gap-2 flex-wrap">
                    {WEEKDAY_LABELS.map((label, idx) => {
                      const day = idx + 1;
                      const selected = reminderForm.business_days.includes(day);
                      return (
                        <button
                          key={day}
                          type="button"
                          onClick={() => setReminderForm((f) => ({
                            ...f,
                            business_days: selected
                              ? f.business_days.filter((d) => d !== day)
                              : [...f.business_days, day].sort(),
                          }))}
                          className={`px-3 py-1.5 rounded text-xs font-medium border transition-colors ${
                            selected
                              ? 'bg-accent-cyan/20 border-accent-cyan text-accent-cyan'
                              : 'bg-bg-surface-2 border-border-subtle text-text-secondary'
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-text-secondary">Reminders are only sent on these days</p>
                </div>
              </div>
            )}

            <Button
              type="button"
              size="sm"
              disabled={updateComplianceMutation.isPending}
              onClick={() => updateComplianceMutation.mutate(reminderForm, {
                onSuccess: () => toast.success('Compliance reminder settings saved'),
                onError: () => toast.error('Failed to save reminder settings'),
              })}
            >
              Save Reminder Settings
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Session Settings — super_admin only */}
      {isSuperAdmin && (
        <Card>
          <CardHeader><CardTitle className="text-sm flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Session Settings</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-text-secondary">Controls session duration for all users in your organisation.</p>
            <div className="space-y-1.5">
              <Label>Session Timeout</Label>
              <select
                value={sessionTimeout}
                onChange={(e) => setSessionTimeout(Number(e.target.value))}
                className="w-full rounded-md border border-border-subtle bg-bg-surface-2 px-3 py-2 text-sm text-text-primary focus:outline-none"
              >
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={60}>1 hour</option>
                <option value={240}>4 hours</option>
                <option value={480}>8 hours</option>
                <option value={1440}>24 hours (default)</option>
              </select>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-primary">Require re-authentication for sensitive actions</p>
                <p className="text-xs text-text-secondary">Role changes, deletions, and API key operations prompt for password</p>
              </div>
              <Toggle checked={requireReauth} onChange={setRequireReauth} />
            </div>
            <Button
              type="button"
              size="sm"
              disabled={updateTenantMutation.isPending}
              onClick={() => updateTenantMutation.mutate(
                { session_timeout_minutes: sessionTimeout, require_reauth_for_sensitive_actions: requireReauth },
                {
                  onSuccess: () => toast.success('Session settings saved'),
                  onError: () => toast.error('Failed to save session settings'),
                },
              )}
            >
              Save Session Settings
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
